import { useEffect, useState } from 'react';
import { Alert, Card, CardContent, Grid, Paper, Stack } from '@mui/material';
import { format } from 'date-fns';
import { Header } from '../Components/Shared/Header/Header';
import { Filters, fieldNames } from '../Components/Shared/Filters/Filters';
import { AvlTable } from '../Components/Shared/Table/Table';
import SkeletonComponent from '../Components/SkeletonComponent';
import { getMaintenanceFolders } from '../Services/Search.services';
import { datetimeToFormattedString } from '../Utils/Utils';
import { dateFormat } from '../Constants/Date.constant';

const rowsPerPage = 10;

const tableHeaders = [
    { label: 'Folder ID', align: 'left' },
    { label: 'Organisation', align: 'left' },
    { label: 'User', align: 'left' },
    { label: 'Maintenance User', align: 'left' },
    { label: 'Performed At', align: 'left' },
];

const isWithinOneDay = (dateStr) => {
    if (!dateStr) return false;

    return Date.now() - new Date(dateStr).getTime() < 24 * 60 * 60 * 1000;
};

const MaintenanceProjects = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [records, setRecords] = useState([]);
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState({
        [fieldNames.text]: '',
        [fieldNames.startDate]: null,
        [fieldNames.endDate]: null,
    });

    useEffect(() => {
        fetchData();
    }, [filters]);

    const fetchData = async () => {
        setIsLoading(true);

        try {
            const data = await getMaintenanceFolders({
                query: filters[fieldNames.text] || undefined,
                startDate: filters[fieldNames.startDate]
                    ? `${format(filters[fieldNames.startDate], dateFormat)}T00:00:00`
                    : undefined,
                endDate: filters[fieldNames.endDate]
                    ? `${format(filters[fieldNames.endDate], dateFormat)}T23:59:59`
                    : undefined,
            });
            const folders = Array.isArray(data) ? data : [];
            const sorted = [...folders].sort(
                (a, b) => new Date(b.last_maintenance_performed_at) - new Date(a.last_maintenance_performed_at)
            );

            setRecords(sorted);
        } catch {
            setRecords([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFiltersChange = (newFilters) => {
        setFilters(newFilters);
        setPage(1);
    };

    const handlePageChange = (_, value) => setPage(value);

    const pageCount = Math.ceil(records.length / rowsPerPage);
    const paginatedRecords = records.slice((page - 1) * rowsPerPage, page * rowsPerPage);

    const tableRows = paginatedRecords.map((row) => {
        const isHighlighted = isWithinOneDay(row.last_maintenance_performed_at) ? { fontWeight: 700 } : undefined;

        return {
            cells: [
                { data: row.folder_id || '-', style: isHighlighted },
                { data: row.organisation || '-', style: isHighlighted },
                { data: row.user_name || '-', style: isHighlighted },
                { data: row.maintenance_user || '-', style: isHighlighted },
                { data: datetimeToFormattedString(row.last_maintenance_performed_at), style: isHighlighted },
            ],
        };
    });

    return (
        <Grid 
            item 
            xs={12} 
            sx={{ mt: 2, mb: 2 }}
        >
            <Card component={Paper}>
                <CardContent>
                    <Header
                        title='Maintenance Projects'
                        filters={
                            <Filters
                                isText
                                isDateRange
                                onFiltersChange={handleFiltersChange}
                            />
                        }
                    />
                    {isLoading ? (
                        <SkeletonComponent />
                    ) : records.length ? (
                        <AvlTable
                            headers={tableHeaders}
                            rows={tableRows}
                            page={page}
                            pageCount={pageCount}
                            rowsPerPage={rowsPerPage}
                            rowsCount={records.length}
                            onPageChange={handlePageChange}
                        />
                    ) : (
                        <Stack 
                            sx={{ width: '100%' }} 
                            spacing={2}
                        >
                            <Alert severity='warning'>No maintenance projects found.</Alert>
                        </Stack>
                    )}
                </CardContent>
            </Card>
        </Grid>
    );
};

export default MaintenanceProjects;
